import React, {useEffect, useMemo, useState} from 'react';
import {Checkbox} from '@mui/material';
import {SVG} from '../../constants/assetLists';
import {highlightSearchMatch} from '../../pages/operations/components/AnnouncementsAndTasks/components/AnnouncementCard';
import {ColumnDef, getValue} from './AppTable';

export type GroupNode<T> = {
  id: string;
  label: string;
  own: T[];
  children?: GroupNode<T>[];
};

export type AppGroupedTableProps<T> = {
  groups: GroupNode<T>[];
  columns: ColumnDef<T>[];
  rowKey: (row: T) => string | number;

  /** Selection (optional) */
  selectable?: boolean;
  selectedKeys?: Array<string | number>;
  onSelectionChange?: (keys: Array<any>) => void;
  rowDisabled?: (row: T) => boolean;

  /** Row className (e.g., disabled rows) */
  rowClassName?: (row: T) => string;

  /** Override the default text-color class per row (overrides disabled text styling) */
  cellTextClass?: (row: T) => string | undefined;

  /** Highlights matches and auto-expands any group whose subtree contains a match */
  searchText?: string;

  /** Table & cell classNames */
  tableClassName?: string;
  headCellBaseClass?: string;
  bodyCellBaseClass?: string;

  /** Rendered centred in the table body when there are no groups */
  emptyComponent?: React.ReactNode;
};

function flattenRowKeys<T>(
  nodes: GroupNode<T>[],
  rowKey: (row: T) => string | number,
): Array<string | number> {
  return nodes.flatMap(node => [
    ...node.own.map(rowKey),
    ...(node.children ? flattenRowKeys(node.children, rowKey) : []),
  ]);
}

function rowMatchesSearch<T>(row: T, term: string, columns: ColumnDef<T>[]) {
  return columns.some(col => {
    if (!col.searchable) return false;
    return String(getValue(row, col.accessor)).toLowerCase().includes(term);
  });
}

function nodeMatchesSearch<T>(
  node: GroupNode<T>,
  term: string,
  columns: ColumnDef<T>[],
): boolean {
  const ownMatch = node.own.some(row => rowMatchesSearch(row, term, columns));
  if (ownMatch) return true;
  return (
    node.children?.some(child => nodeMatchesSearch(child, term, columns)) ??
    false
  );
}

// Path of ids (ancestors first) down to the first node in render order that has own rows.
function findFirstNodeWithOwnPath<T>(nodes: GroupNode<T>[]): string[] | null {
  for (const node of nodes) {
    if (node.own.length > 0) return [node.id];
    if (node.children) {
      const childPath = findFirstNodeWithOwnPath(node.children);
      if (childPath) return [node.id, ...childPath];
    }
  }
  return null;
}

function AppGroupedTable<T>({
  groups,
  columns,
  rowKey,
  selectable,
  selectedKeys = [],
  onSelectionChange,
  rowDisabled,
  rowClassName,
  cellTextClass,
  searchText = '',
  tableClassName,
  headCellBaseClass = 'border-1 border-gray70 px-2',
  bodyCellBaseClass = 'border-1 border-gray70 p-[10px] align-top',
  emptyComponent,
}: AppGroupedTableProps<T>) {
  const [expandedSet, setExpandedSet] = useState<Set<string>>(new Set());

  const toggleExpand = (id: string) => {
    setExpandedSet(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Auto-expand any group whose subtree contains a searchable match — only adds, never collapses.
  useEffect(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return;

    setExpandedSet(prev => {
      const next = new Set(prev);
      const walk = (nodes: GroupNode<T>[]) => {
        nodes.forEach(node => {
          if (nodeMatchesSearch(node, term, columns)) {
            next.add(node.id);
            if (node.children) walk(node.children);
          }
        });
      };
      walk(groups);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, groups]);

  // Expand the first node (in render order) that has own rows, by default.
  useEffect(() => {
    const path = findFirstNodeWithOwnPath(groups);
    if (!path) return;

    setExpandedSet(prev => {
      const next = new Set(prev);
      path.forEach(id => next.add(id));
      return next;
    });
  }, [groups]);

  const allRowKeys = useMemo(
    () => flattenRowKeys(groups, rowKey),
    [groups, rowKey],
  );

  const allChecked =
    selectable &&
    selectedKeys.length > 0 &&
    selectedKeys.length === allRowKeys.length;

  const someChecked =
    selectable &&
    selectedKeys.length > 0 &&
    selectedKeys.length < allRowKeys.length;

  const toggleAll = (checked: boolean) => {
    if (!onSelectionChange) return;
    onSelectionChange(checked ? allRowKeys : []);
  };

  const toggleOne = (key: string | number) => {
    if (!onSelectionChange) return;
    if (selectedKeys.includes(key)) {
      onSelectionChange(selectedKeys.filter(k => k !== key));
    } else {
      onSelectionChange([...selectedKeys, key]);
    }
  };

  const totalCols = columns.length + (selectable ? 1 : 0);

  const renderDataRow = (row: T, depth: number) => {
    const key = rowKey(row);
    const disabled = rowDisabled?.(row) ?? false;
    const isSelected = selectedKeys.includes(key);
    const indentPadding = Math.min(12 + depth * 16, 56);

    return (
      <tr
        key={key}
        className={`transition-all duration-200 ${
          isSelected
            ? 'bg-lightIceBlue hover:shadow-bottom-sm'
            : disabled
              ? 'bg-gray98'
              : 'hover:bg-lightIceBlue hover:shadow-bottom-sm'
        } ${rowClassName?.(row) ?? ''}`}>
        {selectable && (
          <td className="border-1 border-gray70 px-[12px] py-[8px] align-top w-[50px]">
            <Checkbox
              size="small"
              color="secondary"
              checked={isSelected}
              sx={{p: 0, m: 0}}
              onChange={() => toggleOne(key)}
            />
          </td>
        )}

        {columns.map((col, colIdx) => {
          const content = col.render
            ? col.render(row)
            : col.searchable === true
              ? (highlightSearchMatch(
                  String(getValue(row, col.accessor)),
                  searchText,
                ) ?? '')
              : String(getValue(row, col.accessor));
          const cellClasses =
            typeof col.cellClassName === 'function'
              ? col.cellClassName(row)
              : (col.cellClassName ?? '');

          return (
            <td
              key={col.field}
              className={`${bodyCellBaseClass} ${
                cellTextClass?.(row) ?? (disabled ? 'text-gray' : 'text-black')
              } ${cellClasses}`}
              style={
                col.width
                  ? {width: `${col.width}px`, maxWidth: `${col.width}px`}
                  : undefined
              }>
              {colIdx === 0 ? (
                <div style={{paddingInlineStart: `${indentPadding}px`}}>
                  {content}
                </div>
              ) : (
                content
              )}
            </td>
          );
        })}
      </tr>
    );
  };

  const renderGroupNodes = (
    nodes: GroupNode<T>[],
    depth: number,
  ): React.ReactNode[] =>
    nodes.flatMap(node => {
      const expanded = expandedSet.has(node.id);
      const headerPadding = Math.min(12 + depth * 16, 56);

      const headerRow = (
        <tr key={`group-${node.id}`}>
          <td colSpan={totalCols} className="border-1 border-gray70 p-0">
            <div
              className="h-10 flex items-center gap-5"
              style={{
                paddingInlineStart: `${headerPadding}px`,
                paddingInlineEnd: '8px',
              }}>
              <button
                type="button"
                className="w-[12px] h-[12px] shrink-0"
                onClick={() => toggleExpand(node.id)}>
                <img
                  src={expanded ? SVG.expandIcon : SVG.lessThanIcon}
                  alt="caret"
                  className="w-full h-full object-contain"
                />
              </button>
              <span className="text-sm text-black font-medium">
                {node.label}
              </span>
            </div>
          </td>
        </tr>
      );

      if (!expanded) return [headerRow];

      return [
        headerRow,
        ...node.own.map(row => renderDataRow(row, depth + 1)),
        ...(node.children ? renderGroupNodes(node.children, depth + 1) : []),
      ];
    });

  return (
    <div className="flex-1 min-h-0 bg-white pb-4">
      <div className="w-full h-full overflow-auto relative">
        <table
          className={`w-full max-h-full border-collapse ${
            groups.length > 0 ? 'border-2 border-gray70' : ''
          } ${tableClassName ?? ''}`}>
          <thead>
            <tr>
              {selectable && (
                <th className="sticky top-0 z-[1] border-1 border-gray70 px-1 bg-gray95 w-[50px]">
                  <Checkbox
                    size="small"
                    color="secondary"
                    checked={!!allChecked}
                    indeterminate={!!someChecked}
                    onChange={e => toggleAll(e.target.checked)}
                  />
                </th>
              )}

              {columns.map(col => (
                <th
                  key={col.field}
                  className={`${headCellBaseClass} sticky top-0 z-[1] bg-gray95 ${col.headerClassName ?? ''}`}
                  style={
                    col.width
                      ? {width: `${col.width}px`, maxWidth: `${col.width}px`}
                      : undefined
                  }>
                  <div className="flex items-center gap-3">
                    <span>{col.title}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {groups.length === 0 && emptyComponent && (
              <tr>
                <td colSpan={totalCols}>{emptyComponent}</td>
              </tr>
            )}
            {renderGroupNodes(groups, 0)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AppGroupedTable;
